import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertCircle, Loader2, Sliders } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../components/ui/toast";
import { Modal } from "../../components/ui/modal";
import { Input } from "../../components/ui/input";
import { whatsappApi } from "../admin/whatsapp-api";
import type { InboundAgentState, WhatsAppInstance } from "../admin/whatsapp-api";
import { AssignmentFields, useAssignment } from "./assignment-fields";

/** Meta side and LiveKit side in one label: answered calls need both. */
export function waStatusBadge(state?: InboundAgentState | null) {
  const pill = (tone: string, text: string, title?: string | null) => (
    <span title={title || undefined} className={`inline-flex items-center gap-1.5 font-medium text-xs px-2.5 py-1 rounded-md border ${tone}`}>
      {text}
    </span>
  );
  if (!state?.status) return pill("text-amber-700 bg-amber-500/10 border-amber-500/20", "Not configured");
  if (state.status === "agent_attached") return pill("text-slate-600 bg-slate-500/10 border-slate-500/20", "Agent attached");
  if (state.status === "sip_error") {
    return pill("text-red-700 bg-red-500/10 border-red-500/20", state.error === "connecting" || state.error === "rotating" ? "Interrupted; retry" : "SIP error", state.error);
  }
  if (state.route_status === "live") return pill("text-emerald-700 bg-emerald-500/10 border-emerald-500/20", "Live");
  if (state.route_status === "conflict" || state.route_status === "error") {
    return pill("text-red-700 bg-red-500/10 border-red-500/20", "Needs attention", state.route_detail);
  }
  return pill("text-blue-700 bg-blue-500/10 border-blue-500/20", "Connecting");
}

interface ModalProps {
  instance: WhatsAppInstance;
  onClose: () => void;
  onSaved: (instanceName: string, state: InboundAgentState | null) => void;
}

function ConfigureWhatsAppVoiceModal({ instance, onClose, onSaved }: ModalProps) {
  const { token } = useAuth();
  const toast = useToast();
  const [current, setCurrent] = useState<InboundAgentState | null>(instance.inbound_agent || null);
  const assignment = useAssignment(token, {
    agent_id: current?.agent_id || "",
    version_id: current?.version_id || "",
    voice: current?.voice || "",
  });
  const [metaToken, setMetaToken] = useState("");
  const [confirm, setConfirm] = useState<"rotate" | "disconnect" | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Read the live state: the list response carries no route status.
  useEffect(() => {
    if (!token || !instance.inbound_agent) return;
    whatsappApi.readInboundVoice(token, instance.instance_name).then(setCurrent).catch(() => undefined);
  }, [token, instance.instance_name, instance.inbound_agent]);

  const run = async (label: string, fn: () => Promise<InboundAgentState | { detached: boolean }>) => {
    if (!token) return;
    setBusy(label);
    setError(null);
    try {
      const result = await fn();
      const state = "status" in result ? (result as InboundAgentState) : null;
      setCurrent(state);
      onSaved(instance.instance_name, state);
      toast.success(label, "Done");
    } catch (err: any) {
      const msg = err.response?.data?.detail || `${label} failed.`;
      setError(msg);
      toast.danger(label, msg);
    } finally {
      // The Meta token is kept only for the operation it was pasted for.
      setMetaToken("");
      setBusy(null);
    }
  };

  const connected = current?.status === "sip_connected" || current?.status === "sip_error";
  const phoneLabel = current?.number || instance.phone_number || instance.instance_name;
  const shownError = error || assignment.error;
  const button = "inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition disabled:opacity-50";

  return (
    <Modal title="Configure WhatsApp Voice" subtitle={phoneLabel} onClose={onClose} width="max-w-xl">
      {assignment.loading ? (
        <div className="flex flex-col items-center justify-center p-8 gap-3 text-ink-3">
          <Loader2 className="w-6 h-6 animate-spin text-brand" />
          <p className="text-sm">Loading available agents and voices...</p>
        </div>
      ) : (
        <div className="space-y-5">
          {shownError && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 text-xs flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{shownError}</span>
            </div>
          )}

          <div className="p-3.5 rounded-lg border border-line bg-paper-2/60 flex items-center justify-between">
            <div>
              <span className="text-xs font-mono text-ink-3 uppercase tracking-wider block">WhatsApp number</span>
              <span className="text-base font-semibold font-mono text-ink">{phoneLabel}</span>
            </div>
            <div className="text-right space-y-1">
              {waStatusBadge(current)}
              {current?.route_detail && <span className="block text-[11px] text-red-600 max-w-[16rem]">{current.route_detail}</span>}
            </div>
          </div>

          <AssignmentFields idPrefix="wa" assignment={assignment} />

          <div className="flex items-center gap-2">
            <button
              onClick={() => run("Save agent", () => whatsappApi.attachInboundAgent(token!, instance.instance_name, assignment.value))}
              disabled={busy !== null || !assignment.complete}
              className={`${button} border-line bg-paper text-ink hover:border-brand/60 hover:text-brand`}
            >
              {busy === "Save agent" && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Save agent
            </button>
            {current?.status === "agent_attached" && (
              <button
                onClick={() => run("Detach agent", () => whatsappApi.detachInboundAgent(token!, instance.instance_name))}
                disabled={busy !== null}
                className={`${button} border-line bg-paper text-ink hover:border-brand/60 hover:text-brand`}
              >
                Detach agent
              </button>
            )}
          </div>

          {current && (
            <div className="space-y-3 rounded-xl border border-line bg-paper-2/40 p-4">
              <div className="text-xs font-medium text-ink uppercase tracking-wider">WhatsApp calling</div>
              <p className="text-[11px] text-ink-3">
                Connect turns on SIP calling for this number at Meta. Paste a Meta token with
                whatsapp_business_management for each action; it is never stored.
              </p>
              <Input
                type="password"
                autoComplete="off"
                value={metaToken}
                onChange={(e) => setMetaToken(e.target.value)}
                placeholder="Meta user token"
                className="h-9 font-mono"
                aria-label="Meta user token"
              />
              <div className="flex items-center gap-2 flex-wrap">
                {!connected || current.status === "sip_error" ? (
                  <button
                    onClick={() => run("Connect", () => whatsappApi.connectInboundVoice(token!, instance.instance_name, metaToken))}
                    disabled={busy !== null || !metaToken}
                    className={`${button} border-brand bg-brand text-white hover:opacity-90`}
                  >
                    {busy === "Connect" && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    Connect
                  </button>
                ) : null}
                {connected && (
                  <>
                    <button
                      onClick={() => {
                        if (confirm !== "rotate") return setConfirm("rotate");
                        setConfirm(null);
                        run("Rotate password", () => whatsappApi.rotateInboundSecret(token!, instance.instance_name, metaToken));
                      }}
                      disabled={busy !== null || !metaToken}
                      className={`${button} border-amber-500/40 text-amber-700 hover:bg-amber-500/10`}
                    >
                      {confirm === "rotate" ? "Confirm: calls drop for ~1 min" : "Rotate password"}
                    </button>
                    <button
                      onClick={() => {
                        if (confirm !== "disconnect") return setConfirm("disconnect");
                        setConfirm(null);
                        run("Disconnect", () => whatsappApi.disconnectInboundVoice(token!, instance.instance_name, metaToken));
                      }}
                      disabled={busy !== null || !metaToken}
                      className={`${button} border-danger/40 text-danger hover:bg-danger/10`}
                    >
                      {confirm === "disconnect" ? "Confirm disconnect" : "Disconnect"}
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

export function WhatsAppNumbersTab({ searchQuery }: { searchQuery: string }) {
  const { token, user } = useAuth();
  const toast = useToast();
  const [instances, setInstances] = useState<WhatsAppInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [configuring, setConfiguring] = useState<WhatsAppInstance | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      setError(null);
      const all = await whatsappApi.listForClient(token, user?.client_id || user?.id || "");
      // Only Cloud API numbers can take calls; a paired phone (Baileys) cannot.
      setInstances(all.filter((i) => i.integration === "WHATSAPP-BUSINESS"));
    } catch (err: any) {
      const msg = err.response?.data?.detail || "Failed to load WhatsApp numbers.";
      setError(msg);
      toast.danger("Failed to load WhatsApp numbers", msg);
    } finally {
      setLoading(false);
    }
  }, [token, user?.client_id, user?.id, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return instances.filter(
      (i) =>
        !q ||
        [i.phone_number, i.business_id, i.instance_name, i.inbound_agent?.number, i.inbound_agent?.agent_id]
          .some((field) => String(field || "").toLowerCase().includes(q))
    );
  }, [instances, searchQuery]);

  const onSaved = (instanceName: string, state: InboundAgentState | null) =>
    setInstances((prev) => prev.map((i) => (i.instance_name === instanceName ? { ...i, inbound_agent: state } : i)));

  return (
    <div className="rounded-xl border border-line bg-paper overflow-hidden shadow-sm">
      {loading ? (
        <div className="flex flex-col items-center justify-center min-h-[320px] gap-2 text-ink-3">
          <Loader2 className="w-6 h-6 animate-spin text-brand" />
          <p className="text-sm">Loading WhatsApp numbers...</p>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center min-h-[320px] gap-3 text-center p-6">
          <p className="text-sm text-danger">{error}</p>
          <button onClick={load} className="px-3.5 py-1.5 text-xs font-medium rounded-lg border border-danger text-danger hover:bg-danger/10 transition">
            Try Again
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center min-h-[320px] gap-2 text-center p-6 text-ink-3">
          <p className="text-base font-medium text-ink">No WhatsApp Business numbers found</p>
          <p className="text-xs">Add a Cloud API number in WhatsApp Settings first.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-paper-2 border-b border-line text-ink-3 text-xs uppercase tracking-wider font-mono">
              <tr>
                <th className="px-4 py-3">WhatsApp Number</th>
                <th className="px-4 py-3">Inbound Voice</th>
                <th className="px-4 py-3">Agent</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {filtered.map((entry) => {
                const label = entry.inbound_agent?.number || entry.phone_number || entry.instance_name;
                return (
                  <tr key={entry.instance_name} className="hover:bg-paper-2/60 transition">
                    <td className="px-4 py-3 font-mono font-medium text-ink whitespace-nowrap">
                      <div>{label}</div>
                      {entry.business_id && <div className="text-[11px] font-normal text-ink-3">{entry.business_id}</div>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">{waStatusBadge(entry.inbound_agent)}</td>
                    <td className="px-4 py-3 font-mono text-xs text-ink-2 whitespace-nowrap">
                      {entry.inbound_agent?.agent_id ? `${entry.inbound_agent.agent_id} @ ${entry.inbound_agent.version_id}` : "—"}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => setConfiguring(entry)}
                        aria-label={`Configure WhatsApp voice for ${label}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-lg border border-line bg-paper text-ink hover:border-brand/60 hover:text-brand hover:bg-brand/5 shadow-sm transition"
                      >
                        <Sliders className="w-3.5 h-3.5" />
                        Configure Calling
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {configuring && <ConfigureWhatsAppVoiceModal instance={configuring} onClose={() => setConfiguring(null)} onSaved={onSaved} />}
    </div>
  );
}
